import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { isAuthenticated, getUserType, getUser, isCounselor } from '../../../utils/auth';

// Helper to determine the correct dashboard based on user role
const getDashboardForRole = (role, userData) => {
  const businessType = userData?.businessType || userData?.business_type;

  switch (role) {
    case 'student':
      return '/student/dashboard';
    case 'college':
      return '/college/dashboard';
    case 'counselor':
      return '/counselor/dashboard';
    case 'partner':
      if (businessType === 'counselor') return '/counselor/dashboard';
      if (businessType === 'agent') return '/agent/dashboard';
      if (businessType === 'institution') return '/institution/dashboard';
      return '/partner/dashboard';
    case 'admin':
      return '/admin/dashboard';
    default:
      return '/login'; // Fallback
  }
};

export default function ProtectedRoute({ children, allowedUserTypes = ['student', 'partner', 'college'] }) {
  const location = useLocation();
  const [isChecking, setIsChecking] = useState(true);
  const [authStatus, setAuthStatus] = useState({
    isAuth: false,
    shouldRedirect: false,
    redirectPath: null
  });

  useEffect(() => {
    checkAuth();
  }, [location.pathname]);

  const checkAuth = () => {
    // 1. Check Authentication Status
    const isAuth = isAuthenticated();
    
    if (!isAuth) {
      // Not authenticated -> Redirect to Login
      // If the route was for a specific type (e.g., /counselor/dashboard), pre-fill that type
      const loginUrl = allowedUserTypes.length === 1 && allowedUserTypes[0] !== 'student'
        ? `/login?type=${allowedUserTypes[0]}`
        : '/login';

      setAuthStatus({
        isAuth: false,
        shouldRedirect: true,
        redirectPath: loginUrl
      });
      setIsChecking(false);
      return;
    }

    // 2. Get User Data
    const userData = getUser();
    const storedUserType = getUserType();

    // 3. Determine "Effective" User Type
    // Handle the complex case where 'partner' might actually be a 'counselor'
    let effectiveUserType = storedUserType;

    const isUserCounselor = 
      storedUserType === 'counselor' || 
      userData?.businessType === 'counselor' ||
      isCounselor();

    if (isUserCounselor) {
      effectiveUserType = 'counselor';
    }

    console.log('🔍 ProtectedRoute Check:', {
      path: location.pathname,
      storedUserType,
      effectiveUserType,
      allowedUserTypes
    });

    // 4. Check Permission
    let isAllowed = allowedUserTypes.includes(effectiveUserType);

    // Special Handling:
    // If route allows 'partner', also allow 'counselor' (since they are a subset)
    if (isUserCounselor && allowedUserTypes.includes('partner')) {
      isAllowed = true;
    }
    // If route explicitly allows 'counselor', allow them
    if (allowedUserTypes.includes('counselor') && isUserCounselor) {
      isAllowed = true;
    }

    // 5. Handle Denial (Strict Redirection)
    if (!isAllowed) {
      // User is logged in, but accessing a restricted page.
      // Redirect them to THEIR OWN dashboard, not login.
      const correctDashboard = getDashboardForRole(effectiveUserType, userData);
      
      console.warn(`❌ Access Denied: ${effectiveUserType} cannot access ${location.pathname}. Redirecting to ${correctDashboard}`);
      
      setAuthStatus({
        isAuth: true,
        shouldRedirect: true,
        redirectPath: correctDashboard
      });
      setIsChecking(false);
      return;
    }

    // 6. Access Granted
    setAuthStatus({
      isAuth: true,
      shouldRedirect: false,
      redirectPath: null
    });
    setIsChecking(false);
  };

  // Render Loading Spinner while checking
  if (isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Verifying access...</p>
        </div>
      </div>
    );
  }

  // Handle Redirection
  if (authStatus.shouldRedirect && authStatus.redirectPath) {
    return (
      <Navigate 
        to={authStatus.redirectPath} 
        replace 
        // Only save state if redirecting to login; don't save state if bouncing between dashboards
        state={!authStatus.isAuth ? { from: location.pathname } : {}}
      />
    );
  }

  // Render Protected Content
  return children;
}

// Export helper for manual route checking if needed elsewhere
export const checkRouteAccess = (allowedUserTypes = []) => {
  if (!isAuthenticated()) return { allowed: false, redirectTo: '/login' };

  const userData = getUser();
  const storedUserType = getUserType();
  let effectiveUserType = storedUserType;

  if (storedUserType === 'counselor' || userData?.businessType === 'counselor') {
    effectiveUserType = 'counselor';
  }

  let isAllowed = allowedUserTypes.includes(effectiveUserType);
  if (effectiveUserType === 'counselor' && allowedUserTypes.includes('partner')) isAllowed = true;

  if (!isAllowed) {
    return { allowed: false, redirectTo: getDashboardForRole(effectiveUserType, userData) };
  }

  return { allowed: true, userType: effectiveUserType };
};